import type { BoardLead, PipelineStage } from "@school-sales-ai/shared";
import type { GroupedLeads } from "@/hooks/usePipeline";

export type DragAction =
  | { type: "no-op" }
  | { type: "modal"; toStage: string }
  | { type: "move"; toStage: string };

export function resolveDragAction(
  leadId: string,
  toStageId: string,
  grouped: GroupedLeads,
  stages: PipelineStage[],
): DragAction {
  const fromStage = Object.keys(grouped).find((s) =>
    (grouped[s] ?? []).some((l) => l.id === leadId),
  );
  if (!fromStage) return { type: "no-op" };
  return resolveStageChange(fromStage, toStageId, stages);
}

// The one rule for moving a lead between columns, whatever surface asks: the board
// (drag or the phone's arrows) and the lead detail (select or "Cambiar etapa"). A
// column flagged `requiresReason` must go through StageReasonModal from all of
// them — a column that asks for a reason on the board and not on the lead page
// is a rule anyone can walk around.
export function resolveStageChange(
  fromStage: string,
  toStage: string,
  stages: PipelineStage[] | undefined,
): DragAction {
  if (fromStage === toStage) return { type: "no-op" };
  const target = stages?.find((s) => s.key === toStage);
  if (!target) return { type: "no-op" };
  if (target.requiresReason) return { type: "modal", toStage };
  return { type: "move", toStage };
}

// The columns to the right of the lead's own, in board order — what a card's
// «Mover a…» offers. `[]` for the last column, or for a key the board no longer has.
export function nextStages(
  currentKey: string,
  stages: PipelineStage[],
): PipelineStage[] {
  const idx = stages.findIndex((s) => s.key === currentKey);
  return idx === -1 ? [] : stages.slice(idx + 1);
}

// Resolves the target stage key from a droppable id. In swimlane mode ids are
// `${laneKey}::${stageKey}`; a drop into a different lane than the lead's own is
// rejected (cycle is never reassigned by dragging) → returns null. In the flat
// board the id is already the stage key.
export function resolveDropStage(
  overId: string,
  leadId: string,
  leads: Pick<BoardLead, "id" | "cycleId">[],
): string | null {
  if (!overId.includes("::")) return overId;
  const [laneKey, stageKey] = overId.split("::");
  const lead = leads.find((l) => l.id === leadId);
  const sourceLane = lead?.cycleId ?? "none";
  if (sourceLane !== laneKey) return null;
  return stageKey ?? overId;
}

// Is this column the one the client marked as "won"? Kept here beside the other
// drag resolvers, and pure, so the celebration path is unit-testable without
// mounting the board. Never compare against the key `enrolled`: that is only where
// the conversion column starts out, and a renamed one would celebrate nothing.
export function isConversionStage(
  stageKey: string,
  stages: PipelineStage[] | undefined,
): boolean {
  return stages?.some((s) => s.key === stageKey && s.stageRole === "conversion") ?? false;
}
